import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Typography, Tag, Space, Spin, Empty, Button, Collapse, Descriptions, Modal } from 'antd';
import {
  PlayCircleOutlined,
  DashboardOutlined,
  FileTextOutlined,
  ExperimentOutlined,
  RobotOutlined,
  ThunderboltOutlined,
  BarChartOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import KeypointPlayer from '@/pages/activity/vitalityGo/KeypointPlayer';
import FeatureDetailCards from '../FeatureDetailCards';
import SignalWaveform from '../SignalWaveform';
import TraitMappingCards from '../TraitMappingCards';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

const STABILITY_LABELS = {
  trunkTiltRange: '躯干倾角范围',
  trunkTiltStd: '躯干倾角标准差',
  bodyCenterSwayX: '重心横向摆动',
  bodyCenterSwayY: '重心纵向摆动',
};

const STABILITY_UNITS = {
  trunkTiltRange: '°',
  trunkTiltStd: '°',
  bodyCenterSwayX: 'norm',
  bodyCenterSwayY: 'norm',
};

const V3AnalysisTab = ({ roomId, v3Data, loading }) => {
  const [selectedActionIdx, setSelectedActionIdx] = useState(0);
  const videoRef = useRef(null);
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const rafRef = useRef(null);
  const [layer3Data, setLayer3Data] = useState(null);
  const [playingClip, setPlayingClip] = useState(null); // { url, label }
  const [previewImage, setPreviewImage] = useState(null); // { url, label }

  // 用 requestAnimationFrame 持续追踪视频时间
  const trackVideoTime = useCallback(() => {
    if (videoRef.current) {
      setVideoCurrentTime(videoRef.current.currentTime);
    }
    rafRef.current = requestAnimationFrame(trackVideoTime);
  }, []);

  const handleVideoPlay = useCallback(() => {
    rafRef.current = requestAnimationFrame(trackVideoTime);
  }, [trackVideoTime]);

  const handleVideoPause = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    // 暂停时也更新一次
    if (videoRef.current) {
      setVideoCurrentTime(videoRef.current.currentTime);
    }
  }, []);

  const handleVideoSeeked = useCallback(() => {
    if (videoRef.current) {
      setVideoCurrentTime(videoRef.current.currentTime);
    }
  }, []);

  // 清理
  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  // 提前计算 layer3Url（用可选链安全取值，放在 hooks 之前）
  const layer3Url =
    v3Data?.actions?.[selectedActionIdx]?.v3Result?.layerUrls?.layer3;

  // 获取 Layer3 特征数据（含每周期明细）
  useEffect(() => {
    if (!layer3Url) { setLayer3Data(null); return; }
    fetch(layer3Url)
      .then(res => res.json())
      .then(setLayer3Data)
      .catch(() => setLayer3Data(null));
  }, [layer3Url]);

  // 构建每周期明细数据（从 layer3 的 units + features.byUnit 提取）
  const cycleMetricsMap = useMemo(() => {
    if (!layer3Data) return {};
    const units = layer3Data.units || [];
    const byUnit = layer3Data.features?.byUnit || [];
    const map = {};
    units.forEach((u, idx) => {
      const cycleKey = `cycle${u.unitIndex ?? (idx + 1)}`;
      const featureValues = byUnit.find(b => b.unitRef === u.unitId)?.values || [];
      const getFeature = (name) => {
        return featureValues.find(v => v.featureName === name) || null;
      };
      const stages = u.stages || [];
      const eccentricMs = stages
        .filter(s => s.stageType === 'eccentric')
        .reduce((sum, s) => sum + (s.time?.duration || 0), 0) * 1000;
      const concentricMs = stages
        .filter(s => s.stageType === 'concentric')
        .reduce((sum, s) => sum + (s.time?.duration || 0), 0) * 1000;
      map[cycleKey] = {
        rom: getFeature('jointRom'),
        minAngle: getFeature('minAngle'),
        maxAngle: getFeature('maxAngle'),
        durationMs: (u.time?.duration || 0) * 1000,
        eccentricMs: eccentricMs || null,
        concentricMs: concentricMs || null,
        eccentricConcentricRatio:
          eccentricMs && concentricMs ? eccentricMs / concentricMs : null,
        confidence: u.confidence,
        valid: u.valid,
      };
    });
    return map;
  }, [layer3Data]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" tip="V3 分析结果加载中..." />
      </div>
    );
  }

  if (!v3Data || !v3Data.actions || v3Data.actions.length === 0) {
    return (
      <div style={{ padding: 60, textAlign: 'center' }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span style={{ color: '#999', fontSize: 14 }}>
              该课程暂无 V3 分析结果
            </span>
          }
        />
        <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 13 }}>
          V3 分析需要在分析引擎中对视频进行动作评估后才会生成
        </Text>
      </div>
    );
  }

  const actions = v3Data.actions;
  const selectedAction = actions[selectedActionIdx] || {};
  const v3Result = selectedAction.v3Result || {};
  const summary = v3Result.summary || {};
  const result = summary.result || {};
  const clipUrls = v3Result.clipUrls || {};
  const keyframeUrls = v3Result.keyframeUrls || {};
  const reportUrl = v3Result.reportUrl;
  const layerUrls = v3Result.layerUrls || {};
  const aiComment = selectedAction.aiIndicatorsComment;
  const videoUrl = selectedAction.videoUrl;
  const m3u8Url = selectedAction.m3u8Url;
  const coverUrl = selectedAction.coverUrl;
  const jsonlKeypointsUrl = selectedAction.jsonlKeypointsUrl;
  const layer1Url = layerUrls.layer1;
  const layer2Url = layerUrls.layer2;
  const layer4Url = layerUrls.layer4;

  const durationResult = summary.durationResult;
  const countResult = summary.countResult;
  const isCountType = result.resultType === 'count';
  const isDurationType = result.resultType === 'duration';

  // 计算总时长（从视频或分析结果）
  const totalDuration = selectedAction.sportTotalNum
    ? selectedAction.sportTotalNum
    : (durationResult?.validDuration || 0) / 1000;

  return (
    <>
    <div style={{ display: 'flex', gap: 20, minHeight: 560 }}>
      {/* 左侧：动作列表 */}
      <div
        style={{
          width: 260,
          minWidth: 260,
          background: '#fafafa',
          borderRadius: 8,
          padding: '16px 12px',
          border: '1px solid #f0f0f0',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <ExperimentOutlined style={{ color: '#1890ff', marginRight: 6 }} />
          <Text strong style={{ fontSize: 14 }}>
            动作列表
          </Text>
          <Tag color="blue" style={{ marginLeft: 'auto', fontSize: 11 }}>
            {actions.length}
          </Tag>
        </div>
        <div style={{ maxHeight: 580, overflowY: 'auto' }}>
          {actions.map((action, idx) => {
            const aResult = action.v3Result?.summary?.result || {};
            const aIssues = action.v3Result?.issues || [];
            const hasAiComment = !!action.aiIndicatorsComment;
            const isSelected = idx === selectedActionIdx;
            return (
              <div
                key={action.segId || idx}
                onClick={() => setSelectedActionIdx(idx)}
                style={{
                  padding: '10px 12px',
                  marginBottom: 6,
                  borderRadius: 8,
                  cursor: 'pointer',
                  border: isSelected ? '1.5px solid #1890ff' : '1px solid #e8e8e8',
                  background: isSelected ? '#e6f7ff' : '#fff',
                  transition: 'all 0.2s',
                  boxShadow: isSelected ? '0 2px 8px rgba(24,144,255,0.12)' : 'none',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <Tag
                    color={isSelected ? 'blue' : 'default'}
                    style={{ margin: 0, fontSize: 11, lineHeight: '18px', padding: '0 4px' }}
                  >
                    #{idx + 1}
                  </Tag>
                  <Text
                    strong
                    style={{
                      fontSize: 13,
                      color: isSelected ? '#1890ff' : '#333',
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {action.actionName || aResult.displayText || '未知动作'}
                  </Text>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, flexWrap: 'wrap' }}>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    ID:{action.actionId || '--'}
                  </Text>
                  <Tag
                    color={aResult.resultType === 'count' ? 'green' : 'purple'}
                    style={{ margin: 0, fontSize: 10, lineHeight: '16px', padding: '0 4px' }}
                  >
                    {aResult.resultType === 'count' ? '计次' : '计时'}
                  </Tag>
                  {aIssues.length > 0 && (
                    <Tag
                      color="orange"
                      style={{ margin: 0, fontSize: 10, lineHeight: '16px', padding: '0 4px' }}
                    >
                      {aIssues.length}问题
                    </Tag>
                  )}
                  {hasAiComment && (
                    <Tag
                      color="cyan"
                      style={{ margin: 0, fontSize: 10, lineHeight: '16px', padding: '0 4px' }}
                    >
                      AI点评
                    </Tag>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 右侧：详情 */}
      <div style={{ flex: 1, minWidth: 0, overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
        {/* 骨骼动画 */}
        {(jsonlKeypointsUrl || layer1Url) && (
          <div
            style={{
              background: '#fff',
              borderRadius: 10,
              padding: '14px 16px',
              marginBottom: 16,
              border: '1px solid #e8e8e8',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
              <PlayCircleOutlined style={{ color: '#1890ff', marginRight: 6 }} />
              <Text strong style={{ fontSize: 14 }}>骨骼动画回放</Text>
            </div>
            <div style={{ height: 400 }}>
              <KeypointPlayer
                dataUrl={jsonlKeypointsUrl || layer1Url}
                autoPlay={true}
                loop={true}
                onlyShow={true}
              />
            </div>
          </div>
        )}

        {/* AI 大模型点评 */}
        {aiComment && (
          <div
            style={{
              background: 'linear-gradient(135deg, #f0f5ff 0%, #e6f7ff 100%)',
              borderRadius: 10,
              padding: '16px 20px',
              marginBottom: 16,
              border: '1px solid #adc6ff',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
              <RobotOutlined style={{ color: '#1890ff', marginRight: 8, fontSize: 18 }} />
              <Text strong style={{ fontSize: 15, color: '#1890ff' }}>AI 智能点评</Text>
            </div>

            {/* 总评 */}
            {aiComment.summary && (
              <div
                style={{
                  background: '#fff',
                  borderRadius: 8,
                  padding: '12px 16px',
                  marginBottom: 14,
                  borderLeft: '3px solid #1890ff',
                }}
              >
                <Paragraph style={{ fontSize: 14, lineHeight: 1.8, color: '#333', margin: 0 }}>
                  {aiComment.summary}
                </Paragraph>
              </div>
            )}

            {/* 维度评估 */}
            {aiComment.dimensions && aiComment.dimensions.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <Text strong style={{ color: '#1890ff', fontSize: 13, marginBottom: 8, display: 'block' }}>
                  📊 维度分析
                </Text>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
                  {aiComment.dimensions.map((dim, idx) => {
                    const dimColors = {
                      '灵活': { bg: '#f6ffed', border: '#b7eb8f', icon: '🤸' },
                      '控制': { bg: '#e6f7ff', border: '#91d5ff', icon: '🎯' },
                      '力量': { bg: '#fff7e6', border: '#ffd591', icon: '💪' },
                      '稳定': { bg: '#f9f0ff', border: '#d3adf7', icon: '⚖️' },
                      '协调': { bg: '#fff0f6', border: '#ffadd2', icon: '🔄' },
                      '敏捷': { bg: '#e6fffb', border: '#87e8de', icon: '⚡' },
                    };
                    const colorCfg = dimColors[dim.name] || { bg: '#fafafa', border: '#d9d9d9', icon: '📌' };
                    return (
                      <div
                        key={idx}
                        style={{
                          background: colorCfg.bg,
                          borderRadius: 8,
                          padding: '12px 14px',
                          border: `1px solid ${colorCfg.border}`,
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                          <span style={{ fontSize: 18, marginRight: 6 }}>{colorCfg.icon}</span>
                          <Text strong style={{ fontSize: 14 }}>{dim.name}</Text>
                        </div>
                        {/* 观察发现 */}
                        {dim.observations && dim.observations.length > 0 && (
                          <div style={{ marginBottom: dim.movementGuidance?.length ? 8 : 0 }}>
                            <Text style={{ fontSize: 11, color: '#666' }}>🔍 观察：</Text>
                            <ul style={{ margin: '2px 0 0 0', paddingLeft: 16 }}>
                              {dim.observations.map((obs, i) => (
                                <li key={i} style={{ fontSize: 12, color: '#444', lineHeight: 1.6 }}>{obs}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {/* 动作指导 */}
                        {dim.movementGuidance && dim.movementGuidance.length > 0 && (
                          <div>
                            <Text style={{ fontSize: 11, color: '#666' }}>💡 指导：</Text>
                            <ul style={{ margin: '2px 0 0 0', paddingLeft: 16 }}>
                              {dim.movementGuidance.map((g, i) => (
                                <li key={i} style={{ fontSize: 12, color: '#389e0d', lineHeight: 1.6 }}>{g}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 改进建议 */}
            {aiComment.suggestions && aiComment.suggestions.length > 0 && (
              <div>
                <Text strong style={{ color: '#fa8c16', fontSize: 13, marginBottom: 8, display: 'block' }}>
                  💡 改进建议
                </Text>
                <div
                  style={{
                    background: '#fff',
                    borderRadius: 8,
                    padding: '12px 16px',
                    border: '1px solid #e8e8e8',
                  }}
                >
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {aiComment.suggestions.map((s, i) => (
                      <li key={i} style={{ fontSize: 13, color: '#333', lineHeight: 1.8, marginBottom: 4 }}>
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}

        {/* 稳定性指标（计时类动作） */}
        {isDurationType && durationResult?.stabilityMetrics && (
          <div
            style={{
              background: '#fff',
              borderRadius: 8,
              padding: '14px 16px',
              marginBottom: 16,
              border: '1px solid #f0f0f0',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
              <DashboardOutlined style={{ color: '#722ed1', marginRight: 6 }} />
              <Text strong style={{ fontSize: 14 }}>稳定性指标</Text>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
              {Object.entries(durationResult.stabilityMetrics).map(([key, val]) => (
                <div
                  key={key}
                  style={{
                    background: '#f9f0ff',
                    padding: '10px 14px',
                    borderRadius: 8,
                    textAlign: 'center',
                    border: '1px solid #efdbff',
                  }}
                >
                  <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>
                    {STABILITY_LABELS[key] || key}
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#531dab' }}>
                    {typeof val === 'number' ? val.toFixed(2) : val}
                    <span style={{ fontSize: 11, fontWeight: 400, color: '#999', marginLeft: 2 }}>
                      {STABILITY_UNITS[key] || ''}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 计次动作详细指标 */}
        {isCountType && summary.angleCycleResult && (
          <div
            style={{
              background: '#fff',
              borderRadius: 8,
              padding: '14px 16px',
              marginBottom: 16,
              border: '1px solid #f0f0f0',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
              <BarChartOutlined style={{ color: '#52c41a', marginRight: 6 }} />
              <Text strong style={{ fontSize: 14 }}>周期分析指标</Text>
            </div>
            <Descriptions bordered size="small" column={3}>
              <Descriptions.Item label="平均ROM">
                {summary.angleCycleResult.avgRom?.toFixed(1) || '--'}°
              </Descriptions.Item>
              <Descriptions.Item label="ROM一致性">
                {summary.angleCycleResult.romConsistencyCv != null
                  ? summary.angleCycleResult.romConsistencyCv.toFixed(1) + '%'
                  : '--'}
              </Descriptions.Item>
              <Descriptions.Item label="ROM趋势">
                <Tag color={
                  summary.angleCycleResult.romTrend === 'improving' ? 'green' :
                  summary.angleCycleResult.romTrend === 'declining' ? 'red' : 'default'
                }>
                  {summary.angleCycleResult.romTrend === 'improving' ? '↑ 提升' :
                   summary.angleCycleResult.romTrend === 'declining' ? '↓ 下降' : '→ 稳定'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="平均周期时长">
                {summary.angleCycleResult.avgCycleDurationMs != null
                  ? (summary.angleCycleResult.avgCycleDurationMs / 1000).toFixed(2) + 's'
                  : '--'}
              </Descriptions.Item>
              <Descriptions.Item label="节奏(Cadence)">
                {summary.angleCycleResult.cadence?.toFixed(1) || '--'} 次/分
              </Descriptions.Item>
              <Descriptions.Item label="节奏一致性">
                {summary.angleCycleResult.tempoConsistencyCv != null
                  ? summary.angleCycleResult.tempoConsistencyCv.toFixed(1) + '%'
                  : '--'}
              </Descriptions.Item>
              <Descriptions.Item label="平均离心时长">
                {summary.angleCycleResult.avgEccentricMs != null
                  ? (summary.angleCycleResult.avgEccentricMs / 1000).toFixed(2) + 's'
                  : '--'}
              </Descriptions.Item>
              <Descriptions.Item label="平均向心时长">
                {summary.angleCycleResult.avgConcentricMs != null
                  ? (summary.angleCycleResult.avgConcentricMs / 1000).toFixed(2) + 's'
                  : '--'}
              </Descriptions.Item>
              <Descriptions.Item label="离/向比">
                {summary.angleCycleResult.avgEccentricConcentricRatio?.toFixed(2) || '--'}
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}

        {/* Layer 4 运动特性映射 */}
        {layer4Url && <TraitMappingCards layer4Url={layer4Url} />}

        {/* Layer 3 特征详情 */}
        {layer3Url && <FeatureDetailCards layer3Url={layer3Url} />}

        {/* Layer 2 信号波形 */}
        {layer2Url && (
          <SignalWaveform layer2Url={layer2Url} />
        )}

        {/* 关键帧 + 视频片段 */}
        <div
          style={{
            background: 'linear-gradient(135deg, #f8faff 0%, #f0f5ff 50%, #e8f0fe 100%)',
            borderRadius: 12,
            padding: '18px 22px 20px',
            marginBottom: 16,
            border: '1px solid #d6e4ff',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 18 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: 10,
                boxShadow: '0 2px 8px rgba(24,144,255,0.25)',
              }}
            >
              <VideoCameraOutlined style={{ color: '#fff', fontSize: 16 }} />
            </div>
            <Text strong style={{ fontSize: 15, color: '#1a1a1a' }}>关键帧 & 视频片段</Text>
            {Object.keys(keyframeUrls).length > 0 && (
              <span
                style={{
                  marginLeft: 10,
                  fontSize: 11,
                  color: '#8c8c8c',
                  background: 'rgba(24,144,255,0.06)',
                  padding: '3px 10px',
                  borderRadius: 20,
                  border: '1px solid #d6e4ff',
                }}
              >
                共 {Object.keys(keyframeUrls).length} 个周期
              </span>
            )}
          </div>
          {Object.entries(keyframeUrls).length > 0 ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}
            >
              {Object.entries(keyframeUrls)
                .sort(([a], [b]) => {
                  const na = parseInt(a.replace('cycle', ''));
                  const nb = parseInt(b.replace('cycle', ''));
                  return na - nb;
                })
                .map(([cycle, urls], cycleIdx) => {
                  const clipUrl = clipUrls[cycle];
                  const cycleNum = cycle.replace('cycle', '');
                  const mainImg = urls?.[0];
                  const allImgs = urls || [];
                  return (
                    <div
                      key={cycle}
                      style={{
                        display: 'flex',
                        background: '#fff',
                        borderRadius: 10,
                        overflow: 'hidden',
                        border: '1px solid #e8e8e8',
                        transition: 'all 0.25s ease',
                        cursor: clipUrl ? 'pointer' : 'default',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = '0 4px 16px rgba(24,144,255,0.12)';
                        e.currentTarget.style.borderColor = '#91caff';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = 'none';
                        e.currentTarget.style.borderColor = '#e8e8e8';
                      }}
                    >
                      {/* 左侧：关键帧图片 */}
                      <div
                        style={{
                          position: 'relative',
                          width: 160,
                          minWidth: 160,
                          background: '#f0f0f0',
                          flexShrink: 0,
                        }}
                      >
                        {mainImg ? (
                          <img
                            src={mainImg}
                            alt={`${cycle}-main`}
                            onClick={() => setPreviewImage({ url: mainImg, label: `周期 ${cycleNum} - 关键帧` })}
                            style={{
                              width: '100%',
                              height: '100%',
                              minHeight: 120,
                              objectFit: 'cover',
                              display: 'block',
                              cursor: 'pointer',
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              height: 120,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#bbb',
                              fontSize: 12,
                            }}
                          >
                            无关键帧
                          </div>
                        )}
                        {/* 序号角标 */}
                        <div
                          style={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            background: 'rgba(0,0,0,0.65)',
                            color: '#fff',
                            fontSize: 10,
                            fontWeight: 600,
                            padding: '2px 8px',
                            borderRadius: 4,
                          }}
                        >
                          #{cycleNum}
                        </div>
                        {/* 播放按钮覆盖层 */}
                        {clipUrl && (
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              setPlayingClip({ url: clipUrl, label: `周期 ${cycleNum} 片段` });
                            }}
                            style={{
                              position: 'absolute',
                              inset: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: 'rgba(0,0,0,0.25)',
                              opacity: 0,
                              transition: 'opacity 0.2s ease',
                              cursor: 'pointer',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.opacity = 1;
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.opacity = 0;
                            }}
                          >
                            <div
                              style={{
                                width: 38,
                                height: 38,
                                borderRadius: '50%',
                                background: 'rgba(24,144,255,0.85)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 2px 12px rgba(24,144,255,0.4)',
                              }}
                            >
                              <PlayCircleOutlined
                                style={{ color: '#fff', fontSize: 20, marginLeft: 2 }}
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* 右侧：周期信息 + 指标 */}
                      <div style={{ flex: 1, padding: '10px 14px', minWidth: 0 }}>
                        {/* 标题行 */}
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: 8,
                          }}
                        >
                          <span style={{ fontSize: 13, color: '#1a1a1a', fontWeight: 600 }}>
                            周期 {cycleNum}
                          </span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {allImgs.length > 1 && (
                              <span
                                style={{
                                  fontSize: 10,
                                  color: '#8c8c8c',
                                  background: '#f5f5f5',
                                  padding: '1px 6px',
                                  borderRadius: 3,
                                }}
                              >
                                {allImgs.length} 帧
                              </span>
                            )}
                            {clipUrl && (
                              <span
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPlayingClip({ url: clipUrl, label: `周期 ${cycleNum} 片段` });
                                }}
                                style={{
                                  fontSize: 11,
                                  color: '#1890ff',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 3,
                                }}
                              >
                                <PlayCircleOutlined /> 播放片段
                              </span>
                            )}
                          </div>
                        </div>
                        {/* 关键帧缩略图（含第一张） */}
                        {allImgs.length > 1 && (
                          <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                            {allImgs.map((url, i) => (
                              <img
                                key={i}
                                src={url}
                                alt={`${cycle}-${i}`}
                                onClick={() => setPreviewImage({ url, label: `周期 ${cycleNum} - 关键帧 ${i + 1}` })}
                                style={{
                                  width: 48,
                                  height: 36,
                                  objectFit: 'cover',
                                  borderRadius: 4,
                                  border: i === 0 ? '2px solid #1890ff' : '1px solid #e8e8e8',
                                  display: 'block',
                                  cursor: 'pointer',
                                }}
                              />
                            ))}
                          </div>
                        )}
                        {/* 每周期明细指标（横向流式布局） */}
                        {(() => {
                          const detail = cycleMetricsMap[cycle];
                          if (!detail) return null;
                          // 从 byUnit 特征中提取 displayName 和 value
                          const getMetric = (feature, fallbackLabel, color, bg, formatter) => {
                            if (!feature || feature.value == null) return null;
                            return {
                              label: feature.displayName || fallbackLabel,
                              value: formatter(feature.value),
                              color,
                              bg,
                            };
                          };
                          const metrics = [
                            getMetric(detail.rom, 'ROM', '#1890ff', '#e6f7ff', v => v.toFixed(1) + '°'),
                            getMetric(detail.minAngle, '最小角', '#2f54eb', '#f0f5ff', v => v.toFixed(1) + '°'),
                            getMetric(detail.maxAngle, '最大角', '#52c41a', '#f6ffed', v => v.toFixed(1) + '°'),
                            detail.durationMs != null ? { label: '时长', value: (detail.durationMs / 1000).toFixed(2) + 's', color: '#fa8c16', bg: '#fff7e6' } : null,
                            detail.eccentricMs != null ? { label: '离心', value: (detail.eccentricMs / 1000).toFixed(2) + 's', color: '#722ed1', bg: '#f9f0ff' } : null,
                            detail.concentricMs != null ? { label: '向心', value: (detail.concentricMs / 1000).toFixed(2) + 's', color: '#eb2f96', bg: '#fff0f6' } : null,
                            detail.eccentricConcentricRatio != null ? { label: '离/向比', value: detail.eccentricConcentricRatio.toFixed(2), color: '#13c2c2', bg: '#e6fffb' } : null,
                          ].filter(m => m != null);
                          if (metrics.length === 0) return null;
                          return (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                              {metrics.map((m, i) => (
                                <div
                                  key={i}
                                  style={{
                                    background: m.bg,
                                    borderRadius: 4,
                                    padding: '3px 8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 4,
                                  }}
                                >
                                  <span style={{ fontSize: 10, color: '#8c8c8c' }}>{m.label}</span>
                                  <span style={{ fontSize: 12, fontWeight: 600, color: m.color }}>{m.value}</span>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  );
                })}
            </div>
          ) : (
            <Text style={{ fontSize: 13, color: '#8c8c8c' }}>暂无关键帧数据</Text>
          )}
        </div>

        {/* 数据链接区 */}
        <Collapse ghost>
          <Panel header={<Text strong><ThunderboltOutlined /> 原始数据链接</Text>} key="1">
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', padding: '8px 0' }}>
              {reportUrl && (
                <Button type="primary" icon={<FileTextOutlined />} href={reportUrl} target="_blank">
                  完整 HTML 报告
                </Button>
              )}
              {layerUrls.layer1 && (
                <Button href={layerUrls.layer1} target="_blank">Layer1 关键点</Button>
              )}
              {layerUrls.layer2 && (
                <Button href={layerUrls.layer2} target="_blank">Layer2 信号</Button>
              )}
              {layerUrls.layer3 && (
                <Button href={layerUrls.layer3} target="_blank">Layer3 特征</Button>
              )}
              {layerUrls.layer4 && (
                <Button href={layerUrls.layer4} target="_blank">Layer4 特性</Button>
              )}
              {layerUrls.llmPayload && (
                <Button href={layerUrls.llmPayload} target="_blank">LLM Payload</Button>
              )}
            </div>
          </Panel>
        </Collapse>
      </div>
    </div>

    {/* 视频片段播放弹窗 */}
    <Modal
      title={playingClip?.label || '视频片段'}
      open={!!playingClip}
      footer={null}
      width={720}
      destroyOnClose
      onCancel={() => setPlayingClip(null)}
    >
      {playingClip?.url && (
        <video
          src={playingClip.url}
          controls
          autoPlay
          style={{ width: '100%', borderRadius: 8, background: '#000', maxHeight: '70vh' }}
        />
      )}
    </Modal>

    {/* 关键帧图片放大弹窗 */}
    <Modal
      title={previewImage?.label || '关键帧'}
      open={!!previewImage}
      footer={null}
      width={800}
      destroyOnClose
      onCancel={() => setPreviewImage(null)}
    >
      {previewImage?.url && (
        <img
          src={previewImage.url}
          alt={previewImage.label}
          style={{ width: '100%', borderRadius: 8, display: 'block' }}
        />
      )}
    </Modal>
    </>
  );
};

export default V3AnalysisTab;
